import type {
  Resource,
  ResourceList,
  Role,
  RoleList,
} from "@eai-toolkit/client";
import { RoleApi, TeamApi } from "@eai-toolkit/client";
import { filtersToQ } from "@store/utils/queryUtils";
import { constructOrderParameter } from "@store/utils/sortUtils";

import { hydrateRolesQueryStateFromString } from "../queryState";
import { baseApi, handleQueryError } from "./baseApi";
import { axiosInstance, basePath, getAllResources } from "./utils";

export type { Role };

const roleApi = new RoleApi(undefined, undefined, axiosInstance);
const teamApi = new TeamApi(undefined, undefined, axiosInstance);

// Neither member endpoint honours `order` (verified against prod), so none is
// sent; paging is stable by id and display order is the consumer's job.

// The collapsed member summary needs the total and whether listing is
// permitted, not the rows.
const COUNT_ONLY_LIMIT = 1;

export const rolesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getRoleByFullName: builder.query<Role, { fullName: string }>({
      queryFn: async ({ fullName }) => {
        try {
          // v1RoleGetById accepts either an ID or a full name
          const { data } = await roleApi.v1RoleGetById({ roleId: fullName });
          return { data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { fullName }) => [
        { type: "Role", id: fullName },
      ],
    }),

    searchRoles: builder.query<RoleList, { queryKey: string }>({
      queryFn: async ({ queryKey }) => {
        try {
          const queryState = hydrateRolesQueryStateFromString(queryKey);
          const {
            paging: { offset, limit },
            query: { filters, sort },
          } = queryState;

          const order = constructOrderParameter(sort.fieldName, sort.direction);

          const response = await roleApi.v1ClusterRoleGet({
            offset,
            limit,
            q: filtersToQ(filters),
            order,
          });

          return { data: response.data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...(result.items ?? []).map(({ id }) => ({
                type: "Role" as const,
                id,
              })),
              { type: "Role", id: "SEARCH_LIST" },
            ]
          : [{ type: "Role", id: "SEARCH_LIST" }],
    }),

    getRoleById: builder.query<Role, { id: string }>({
      queryFn: async ({ id }) => {
        try {
          const { data } = await roleApi.v1RoleGetById({ roleId: id });
          return { data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { id }) => [{ type: "Role", id }],
    }),

    getTeamMembers: builder.query<
      { members: Resource[]; total: number },
      { teamId: string; type?: string; all?: boolean }
    >({
      queryFn: async ({ teamId, type, all }) => {
        try {
          if (!all) {
            // Only fetch the count, not the full list of members.
            const { data } = await teamApi.v1TeamMemberGet({
              teamId,
              type,
              human: true,
              limit: COUNT_ONLY_LIMIT,
            });
            const list = data as ResourceList;
            return {
              data: { members: [], total: list.metadata?.total ?? 0 },
            };
          }
          // If all members are requested, fetch them all.
          // As of writing this comment it'll be a max of 978 - large but not
          // insanely large as to cause performance issues. We can reevaluate if
          // this ever becomes an issue.
          const { items, metadata } = await getAllResources<Resource>(
            (params) =>
              teamApi.v1TeamMemberGet({
                teamId,
                type,
                human: true,
                ...params,
              }) as unknown as Promise<{ data: ResourceList }>,
          );
          return { data: { members: items, total: metadata.total } };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { teamId }) => [
        { type: "Team" as const, id: teamId },
      ],
    }),
    getRoleMembers: builder.query<
      Resource[],
      { roleId: string; type?: string }
    >({
      queryFn: async ({ roleId, type }) => {
        try {
          const { items } = await getAllResources<Resource>(
            (params) =>
              roleApi.v1RoleMemberGet({
                roleId,
                type,
                human: true,
                ...params,
              }) as unknown as Promise<{ data: ResourceList }>,
          );
          return { data: items };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (_result, _error, { roleId }) => [
        { type: "Role" as const, id: roleId },
      ],
    }),

    getMyMemberships: builder.query<Resource[], { userId: string }>({
      queryFn: async ({ userId }) => {
        try {
          const { data } = await axiosInstance.get<Resource[]>(
            `${basePath}/v1/user/${userId}/membership`,
          );
          return { data };
        } catch (error) {
          return handleQueryError(error);
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ id }) => ({ type: "Role" as const, id })),
              { type: "Role", id: "MEMBERSHIPS" },
            ]
          : [{ type: "Role", id: "MEMBERSHIPS" }],
    }),
  }),
});

export const {
  useGetRoleByFullNameQuery,
  useGetTeamMembersQuery,
  useGetRoleMembersQuery,
  useLazyGetRoleByFullNameQuery,
  useSearchRolesQuery,
  useGetRoleByIdQuery,
  useGetMyMembershipsQuery,
} = rolesApi;
